import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FiChevronDown } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface Recipient {
  id: number;
  name: string;
  email: string;
  segment: string;
  tags: string[];
  lastOpened: string;
  lastClicked: string;
}

interface RecipientsProps {
  recipients: Recipient[];
  c?: string;
  fromtab?: string;
}

const Recipients: React.FC<RecipientsProps> = ({ recipients, c, fromtab }) => {
  // State for current page in pagination
  const [currentPage, setCurrentPage] = useState(1);
  // State for selected recipient IDs (for export functionality)
  const [selected, setSelected] = useState<number[]>([]);
  // State for toggling export dropdown visibility
  const [exportOpen, setExportOpen] = useState(false);
  const navigate = useNavigate();

  const itemsPerPage = 6;

  // Paginates the recipient data
  const totalPages = Math.ceil(recipients.length / itemsPerPage);
  const paginatedData = recipients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Toggles selection of all recipients on the current page
  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(paginatedData.map((r) => r.id));
    } else {
      setSelected([]);
    }
  };

  // Toggles selection of a single recipient by ID
  const toggleSelect = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
    );
  };

  // Exports selected recipients as a CSV file
  const exportCSV = () => {
    const rows = recipients.filter((r) => selected.includes(r.id));
    const header = [
      'Name',
      'Email',
      'Segment',
      'Tags',
      'Last Opened',
      'Last Clicked',
    ];
    const body = rows
      .map((r) =>
        [
          r.name,
          r.email,
          r.segment,
          r.tags.join('|'),
          r.lastOpened,
          r.lastClicked,
        ].join(',')
      )
      .join('\n');

    const csvContent = [header.join(','), body].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'recipients.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Exports selected recipients as a PDF file using jsPDF
  const exportPDF = () => {
    const rows = recipients.filter((r) => selected.includes(r.id));
    const doc = new jsPDF();
    autoTable(doc, {
      head: [
        ['Name', 'Email', 'Segment', 'Tags', 'Last Opened', 'Last Clicked'],
      ],
      body: rows.map((r) => [
        r.name,
        r.email,
        r.segment,
        r.tags.join(', '),
        r.lastOpened,
        r.lastClicked,
      ]),
    });
    doc.save('recipients.pdf');
  };

  return (
    <div className='bg-white rounded-md mt-6 border shadow-sm'>
      {/* Header with Export */}
      <div className='flex justify-between items-center px-6 py-4'>
        <h2 className='text-lg font-medium text-gray-700'>Recipients</h2>
        <div className='relative'>
          <button
            onClick={() => setExportOpen((prev) => !prev)}
            disabled={selected.length === 0}
            className={`px-4 py-2 rounded-sm text-sm font-medium flex items-center text-white ${
              selected.length === 0
                ? 'bg-gray-200 cursor-not-allowed'
                : 'bg-orange-500'
            }`}
          >
            Export <FiChevronDownComponent className='ml-2' />
          </button>
          {exportOpen && selected.length > 0 && (
            <div className='absolute right-0 mt-2 w-32 bg-white border rounded-md shadow-lg z-10'>
              <button
                onClick={() => {
                  exportPDF();
                  setExportOpen(false);
                }}
                className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
              >
                Export as PDF
              </button>
              <button
                onClick={() => {
                  exportCSV();
                  setExportOpen(false);
                }}
                className='block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left'
              >
                Export as CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className='overflow-x-auto mx-6 border border-gray-200 shadow-sm rounded-md'>
        <table className='min-w-full text-sm rounded-md overflow-hidden '>
          <thead className='bg-gray-100'>
            <tr className='text-gray-600 text-sm border-b'>
              <th className='px-4 py-3 text-left'>
                <input
                  type='checkbox'
                  onChange={toggleSelectAll}
                  checked={
                    selected.length === paginatedData.length &&
                    paginatedData.length > 0
                  }
                  className='w-4 h-4'
                />
              </th>
              <th className='px-4 py-3 text-[#657584] text-[12px] font-normal text-left'>
                RECIPIENT
              </th>
              <th className='px-4 py-3 text-[#657584] text-[12px] font-normal text-left'>
                SEGMENT
              </th>
              <th className='px-4 py-3 text-[#657584] text-[12px] font-normal text-left'>
                TAGS
              </th>
              <th className='px-4 py-3 text-[#657584] text-[12px] font-normal text-left'>
                LAST OPENED
              </th>
              <th className='px-4 py-3 text-[#657584] text-[12px] font-normal text-left'>
                LAST CLICKED
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((recipient) => (
              <tr key={recipient.id} className='border-b hover:bg-gray-50'>
                <td className='px-4 py-2'>
                  <input
                    type='checkbox'
                    checked={selected.includes(recipient.id)}
                    onChange={() => toggleSelect(recipient.id)}
                    className='w-4 h-4'
                  />
                </td>
                <td
                  className='px-4 py-2 hover:text-teal-600 hover:underline cursor-pointer'
                  onClick={() => {
                    navigate(
                      `/reports/${fromtab}/view/${c}/recipients/${recipient.id}`,
                      { state: { recipient, c, fromtab } }
                    );
                  }}
                >
                  <div className='flex items-center gap-2'>
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                        recipient.name
                      )}&background=random`}
                      alt={recipient.name}
                      className='w-8 h-8 rounded-full'
                    />
                    <div>
                      <p className='font-medium text-gray-800'>
                        {recipient.name}
                      </p>
                      <p className='text-gray-500 text-xs'>{recipient.email}</p>
                    </div>
                  </div>
                </td>
                <td className='px-4 py-2'>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      recipient.segment === 'New User'
                        ? 'bg-green-100 text-green-600'
                        : recipient.segment === 'Old User'
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {recipient.segment}
                  </span>
                </td>
                <td className='px-4 py-2'>
                  {recipient.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className='inline-block bg-gray-100 text-gray-600 px-2 py-0.5 text-xs rounded-full mr-1'
                    >
                      {tag}
                    </span>
                  ))}
                </td>
                <td className='px-4 py-2 text-gray-700'>
                  {recipient.lastOpened}
                </td>
                <td className='px-4 py-2 text-gray-700'>
                  {recipient.lastClicked}
                </td>
              </tr>
            ))}
            {paginatedData.length === 0 && (
              <tr>
                <td colSpan={6} className='px-6 py-6 text-center text-gray-500'>
                  No recipients available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className='flex items-center justify-between px-6 py-4 text-sm text-gray-500'>
        <span>
          Showing {paginatedData.length} results out of {recipients.length}
        </span>
        {totalPages > 1 && (
          <div className='flex items-center gap-2'>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className={`px-3 py-1 border rounded ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                  : 'bg-orange-500 text-white'
              }`}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentPage === i + 1
                    ? 'bg-orange-500 text-white'
                    : 'bg-white text-gray-600'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className={`px-3 py-1 border rounded ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                  : 'bg-orange-500 text-white'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Export the Recipients component as the default export
export default Recipients;
